import { useState } from "react";
import { FileDown, LoaderCircle } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/components/ui/tooltip";
import {
  filterLeadsForExport,
  LEADS_EXPORT_TRUNCATED_HEADER,
  leadsExportHref,
  type LeadsExportFilter,
  type LeadsExportQuery,
} from "@shared/lib/leads-export";
import type { Lead } from "@shared/lib/leads-shared";
import { INTL_LOCALE } from "@shared/i18n";
import { getLocale, t, useT } from "@shared/client/locale";

/**
 * Télécharge l'export par fetch plutôt que par un lien `download` (comme
 * l'export PDF) : une session expirée ou une erreur du serveur s'affiche au
 * lieu d'être enregistrée comme fichier, et la page ne change pas. Renvoie un
 * avertissement quand le serveur a tronqué le fichier, sinon une chaîne vide.
 */
export async function downloadLeadsExport(query: LeadsExportQuery): Promise<string> {
  const response = await fetch(leadsExportHref(query));
  if (!response.ok || !response.headers.get("content-type")?.includes("text/csv")) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error || t().leads.export.failed);
  }
  const filename = /filename="([^"]+)"/.exec(response.headers.get("content-disposition") ?? "")?.[1] ?? "hune-leads.csv";
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);

  const limit = Number(response.headers.get(LEADS_EXPORT_TRUNCATED_HEADER));
  return limit > 0 ? t().leads.export.truncated(limit.toLocaleString(INTL_LOCALE[getLocale()])) : "";
}

/**
 * « Exporter en CSV » : la sélection de l'écran (un statut ou les archivés,
 * les favoris, la recherche) pour Excel ou Google Sheets. Pas de toast :
 * l'export est immédiat ; seul un échec, ou un fichier tronqué, se signale.
 */
export function LeadsExportButton({
  leads,
  filter,
  favoritesOnly,
  search,
  onError,
}: {
  leads: Lead[];
  filter: LeadsExportFilter;
  favoritesOnly: boolean;
  search: string;
  /** Message à afficher par l'écran (échec, fichier tronqué) ; vide pour l'effacer. */
  onError: (message: string) => void;
}) {
  const copy = useT().leads.export;
  const [busy, setBusy] = useState(false);
  const query = { filter, favoritesOnly, search };
  const count = filterLeadsForExport(leads, query).length;

  async function exportLeads() {
    setBusy(true);
    onError("");
    try {
      onError(await downloadLeadsExport(query));
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : copy.failedShort);
    } finally {
      setBusy(false);
    }
  }

  const label = (
    <>
      {busy ? (
        <LoaderCircle aria-hidden="true" className="animate-spin text-muted-foreground" />
      ) : (
        <FileDown aria-hidden="true" className="text-muted-foreground" />
      )}
      <span className="max-sm:sr-only">{copy.button}</span>
    </>
  );

  if (count === 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Un bouton désactivé ne reçoit pas le survol : l'infobulle tient à son enveloppe. */}
          <span className="inline-flex" tabIndex={0}>
            <Button disabled size="sm" type="button" variant="outline">
              {label}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {leads.length === 0 ? copy.emptyPipeline : copy.emptyFiltered}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Button
      disabled={busy}
      onClick={exportLeads}
      size="sm"
      title={copy.title(count)}
      type="button"
      variant="outline"
    >
      {label}
    </Button>
  );
}
